/* eslint-disable no-else-return */
import { useCallback } from 'react'
import localStore from 'store'
import axios from 'axios'
import dayjs from 'dayjs'
import {
  clone,
  debounce,
  find,
  includes,
  isArray,
  isEmpty,
  isEqual,
  last,
  toLower,
  uniq,
} from 'lodash-es'
import { parse } from 'query-string'
import { store as reduxStore } from '@/store'
import {
  BILLING_PLAN_ID,
  discountTypes,
  DUMMY_COMPANY_ID,
  experimentalWebsiteIdBrowsershot,
  PRODUCT_VARIANT_MAX_OPTIONS,
} from './static'
import { staticConst as bankStaticConst } from '@/containers/FinanceBanks/static'

export const nth = (n) => {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100

  return n + (s[(v - 20) % 10] || s[v] || s[0])
}

export const arraymove = (list, startIndex, endIndex) => {
  const result = Array.from(list)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)
  return result
}

export const priceValidator = (rule, value) => {
  if (value <= 0) {
    return Promise.reject(rule.message)
  }
  return Promise.resolve()
}

export const disabledTransDate = (current) => {
  const { options } = reduxStore.store.getState().options

  if (!options.lock_date) {
    return false
  }

  return current && current.isSameOrBefore(dayjs(options.lock_date), 'day')
}

export const transDateValidator = (rule, value) => {
  const { options } = reduxStore.store.getState().options
  // if (!options.lock_date) {
  //   callback();
  // }

  if (value && options.lock_date && value.isSameOrBefore(dayjs(options.lock_date), 'day')) {
    return Promise.reject(rule.message)
  }
  return Promise.resolve()
}

// Get import template link
export const getTemplateUrl = (fileName = '') => {
  return `${generateBaseUri()}/download/import/${fileName}`
}

export const generateBaseUri = ({ ignoreInitEndpoint = false } = {}) => {
  // ignoreInitEndpoint hanya men-skip per-company init endpoint (app.endpoint).
  // Override manual base URL dari ChangeEnvModal (app.bypassEndpoint) tetap dihormati.

  const byPassEndpoint = localStore.get('app.bypassEndpoint')
  if (localStore.get('app.isOverwriteBaseUrl') && !!byPassEndpoint) {
    return byPassEndpoint
  }

  let apiUrl = byPassEndpoint || import.meta.env.VITE_API_URL
  let baseUrl = apiUrl ? apiUrl.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '') : ''
  const { hostname } = window.location
  const appEndpoint = ignoreInitEndpoint ? null : localStore.get('app.endpoint')
  if (import.meta.env.PROD) {
    const splitHostname = hostname.split('.')
    if (splitHostname[0] && appEndpoint) {
      baseUrl = `${splitHostname[0]}.${baseUrl}`
    }
  }

  let protocol = includes(apiUrl, 'https://') ? 'https://' : 'http://'
  if (includes('local.jokolodang.com', hostname)) {
    protocol = 'http://'
  }
  if (appEndpoint) {
    apiUrl = `${protocol}${appEndpoint}/api/v1`
  } else {
    apiUrl = `${protocol}${baseUrl}`
    if (includes('local.jokolodang.com', hostname)) {
      apiUrl = `http://app.kledo.test/api/v1`
    } else if (includes('localssl.jokolodang.com', hostname)) {
      apiUrl = `https://app.kledo.test/api/v1`
    }
  }
  return apiUrl
}

export const getMax = (arr = []) => {
  let max
  arr.forEach((e, i) => {
    if (i === 0) {
      max = e
    }
    if (max < e) {
      max = e
    }
  })
  return max
}

export const cleanBlankValue = (obj) => {
  Object.keys(obj).forEach((key) => {
    if (obj[key] === null || obj[key] === undefined || obj[key] === '') {
      delete obj[key]
    }
  })

  return obj
}

export const normalizeSerialQtyDiff = (qty, totalQty) => {
  const diffQty = (Number(qty) || 0) - (Number(totalQty) || 0)

  return parseFloat(diffQty.toFixed(10))
}

// Create custom axios GET with cancellation
export const makeGetRequestCreator = () => {
  // Track cancellation tokens per URL
  // Agar state cancel tidak shared di antara request meskipun beda url
  const activeCalls = new Map()
  return (url, { forceCancellation, forceCancellationWithURL, ...options } = {}) => {
    const existingCall = activeCalls.get(url)
    if (existingCall) {
      if ((!forceCancellation && !forceCancellationWithURL) || forceCancellationWithURL) {
        existingCall.cancel('Only one request allowed at a time.')
      }
    }
    const call = axios.CancelToken.source()
    activeCalls.set(url, call)
    return axios
      .get(url, {
        cancelToken: call.token,
        ...options,
      })
      .finally(() => {
        // Clean up after request completes
        activeCalls.delete(url)
      })
  }
}

export const getOnce = makeGetRequestCreator()

// To check layout is desktop
export const isDesktopLayout = () => {
  const { isMobileView, isTabView } = reduxStore.store.getState().settings
  // const isMobileView = localStore.get('app.settings.isMobileView');
  // const isTabView = localStore.get('app.settings.isTabView');

  return !isMobileView && !isTabView
}

export const discountType = (type) => {
  const { options } = reduxStore.store.getState().options
  let isDiscountNumeric
  if (type === 'purchase') {
    isDiscountNumeric = options.purchase_discount_numeric === 1
  } else {
    isDiscountNumeric = options.sale_discount_numeric === 1
  }

  return isDiscountNumeric ? 'discount_amount' : 'discount_percent'
}

// Field diskon aktif per item: discount_type 1 = persen, 2 = nominal per unit, null = ikut setting global
export const getItemDiscountField = (item, globalField) => {
  if (item?.discount_type === discountTypes.PERCENT) {
    return 'discount_percent'
  }
  if (item?.discount_type === discountTypes.AMOUNT) {
    return 'discount_amount'
  }
  return globalField
}

// Item bertipe dari API: discount_amount di response mengikuti arti lama (setting global),
// jadi isi ulang discount_amount per unit dari discount_amount_input / discount_percent
export const normalizeItemDiscount = (item, price = item?.price) => {
  if (
    item?.discount_type !== discountTypes.AMOUNT &&
    item?.discount_type !== discountTypes.PERCENT
  ) {
    return item
  }
  // discount_percent tipe 2 berisi persen turunan server, dipakai bila discount_amount_input tidak ada
  const percentAmount = (parseFloat(price || 0) * parseFloat(item.discount_percent || 0)) / 100
  if (item.discount_type === discountTypes.AMOUNT && item.discount_amount_input != null) {
    return { ...item, discount_amount: parseFloat(item.discount_amount_input) }
  }
  return { ...item, discount_amount: percentAmount }
}

// Item turunan (mis. retur) mengikuti jenis diskon item tagihan asal bila belum membawanya sendiri
export const inheritItemDiscountType = (item, parentItem) => {
  if (item?.discount_type || !parentItem?.discount_type) {
    return item
  }
  // Nominal per unit retur sama persis dengan tagihan (BE), jadi ikut nilai input induk
  return {
    ...item,
    discount_type: parentItem.discount_type,
    discount_amount_input: parentItem.discount_amount_input,
  }
}

// Nominal diskon per unit, dipakai untuk sorter kolom diskon dengan baris campuran
export const getItemDiscountPerUnit = (item, globalField) => {
  if (getItemDiscountField(item, globalField) === 'discount_amount') {
    return parseFloat(item?.discount_amount || 0)
  }
  return (parseFloat(item?.price || 0) * parseFloat(item?.discount_percent || 0)) / 100
}

export const openNewTabBlobFile = (data) => {
  const file = new Blob([data], { type: 'application/pdf' })
  // Build a URL from the file
  const fileURL = URL.createObjectURL(file)
  const tempLink = document.createElement('a')
  tempLink.href = fileURL
  tempLink.setAttribute('target', '_blank')
  tempLink.click()
}

export const openNewTab = (moduleUrl = '', withBaseUri = true) => {
  const tempLink = document.createElement('a')
  if (withBaseUri) {
    tempLink.href = `${generateBaseUri()}${moduleUrl}`
  } else {
    tempLink.href = moduleUrl
  }
  tempLink.setAttribute('target', '_blank')
  tempLink.click()
}

export const openNewTabBrowsershot = ({ url, websiteId, withBaseUri = true }) => {
  const withBrowsershot = experimentalWebsiteIdBrowsershot.includes(websiteId)

  openNewTab(
    `${url}${
      withBrowsershot ? (url.includes('?') ? '&use_browsershot=1' : '?use_browsershot=1') : ''
    }`,
    withBaseUri,
  )
}

export const filterOption = (input, option) => {
  if (option.children) {
    return option.children.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0
  }
  if (option?.name) {
    return option?.name.toLowerCase().indexOf(input.toLowerCase()) >= 0
  }
  if (option?.label) {
    return option.label.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0
  }
  return true
}

export const copyclip = (text) => {
  const textField = document.createElement('textarea')
  textField.innerText = text
  document.body.appendChild(textField)
  textField.select()
  document.execCommand('copy')
  textField.remove()
}

export const clearCache = (reload = false) => {
  if ('caches' in window) {
    caches.keys().then((names) => {
      const delAll = new Promise((resolve) => {
        if (names.length === 0) {
          resolve()
        }
        names.forEach(async (name, i) => {
          await caches.delete(name)
          if (i === names.length - 1) {
            resolve()
          }
        })
      })

      delAll.then(() => {
        if (reload) {
          window.location.reload(true)
        }
      })
    })
  }
}

/**
 * Determine the mobile operating system.
 * This function returns one of 'ios', 'android', 'windows_phone', or 'other'.
 *
 * @returns {String}
 */
export const getMobileOperatingSystem = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera

  // Windows Phone must come first because its UA also contains "Android"
  if (/windows phone/i.test(userAgent)) {
    return 'windows_phone'
  }

  if (/android/i.test(userAgent)) {
    return 'android'
  }

  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return 'ios'
  }

  return 'other'
}

export const useDebounce = (callback, delay) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useCallback(
    debounce((...args) => callback(...args), delay),
    [delay], // will recreate if delay changes
  )
}

export const parseUrlToFileName = (url) => {
  let name = ''
  if (url) {
    try {
      const { pathname } = new URL(url)
      if (pathname) {
        const splitPath = pathname.split('/')
        name = splitPath ? decodeURI(last(splitPath)) : ''
      }
    } catch (error) {
      // Jika URL tidak valid, coba ekstrak nama file dari string url langsung
      const splitPath = url.split('/')
      name = splitPath ? decodeURI(last(splitPath)) : ''
    }
  }
  return name
}

// Replace slash for uri
export const replaceSlash = (text = '', replaceable = ':slash:') => text.replace(/\//g, replaceable)

// Restore slash for uri
export const restoreSlash = (text = '', replaceable = '/') => text.replace(/:slash:/g, replaceable)

/**
 * Cek apakah sebuah error adalah penolakan 403 dari BE.
 *
 * Interceptor axios me-reject dengan `error.response`, jadi statusnya ada di `error.status`.
 * Dipakai untuk penolakan yang harus ditampilkan sebagai halaman / inline, bukan toast biasa —
 * mis. pembatasan akses transaksi berdasarkan usia.
 *
 * @param error {Object}
 * @return {boolean}
 */
export const isForbiddenError = (error) => (error?.status ?? error?.response?.status) === 403

/**
 * Ambil pesan penolakan dari BE, apa pun bentuk errornya.
 *
 * @param error {Object}
 * @return {string|undefined}
 */
export const getServerErrorMessage = (error) =>
  error?.data?.message ?? error?.response?.data?.message ?? error?.serverMessage

export const isPaidFeature = ({ billing, code }) => {
  const hiddenFeatures = [
    ...(billing.hidden_feature_trial ?? []),
    ...(billing.hidden_feature ?? []),
    ...(billing.hidden_feature_elite ?? []),
    ...(billing.hidden_feature_champion ?? []),
  ]

  return hiddenFeatures.filter((feature) => feature === code).length > 0
}
export const isFeaturePro = ({ billing, code }) =>
  billing?.hidden_feature && billing.hidden_feature.filter((e) => e === code).length > 0

export const isFeatureElite = ({ billing, code }) =>
  billing?.hidden_feature_elite && billing.hidden_feature_elite.filter((e) => e === code).length > 0

export const isFeatureChampion = ({ billing, code }) =>
  billing?.hidden_feature_champion &&
  billing.hidden_feature_champion.filter((e) => e === code).length > 0

// Batas maksimal opsi varian produk sesuai paket aktif (berdasarkan billing.plan_id).
// Fitur varian minimal paket Elite, jadi default ke Elite untuk paket terendah yang bisa akses.
export const getMaxProductVariantOptions = (billing) => {
  const planId = Number(billing?.plan_id)
  if ([BILLING_PLAN_ID.CHAMPION_TRIAL, BILLING_PLAN_ID.CHAMPION].includes(planId)) {
    return PRODUCT_VARIANT_MAX_OPTIONS.CHAMPION
  }
  return PRODUCT_VARIANT_MAX_OPTIONS.ELITE
}

export const makeYears = (min = 2000) => {
  const years = []
  for (let i = dayjs().year(); i >= min; i -= 1) {
    years.push(i.toString())
  }

  return years
}

export function getBase64(img, callback) {
  const reader = new FileReader()
  reader.addEventListener('load', () => callback(reader.result))
  reader.readAsDataURL(img)
}

export function getDimensions(file) {
  return new Promise((resolve, reject) => {
    const img = document.createElement('img')
    img.onload = () => {
      resolve({
        height: img.naturalHeight,
        width: img.naturalWidth,
      })
    }
    img.onerror = reject

    const reader = new FileReader()
    reader.onloadend = (ended) => {
      img.src = ended.target.result
    }
    reader.readAsDataURL(file)
  })
}

export function getDimensionsFromUrl(url) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      resolve({
        height: img.naturalHeight,
        width: img.naturalWidth,
      })
    }
    img.onerror = reject
    img.src = url
  })
}

// export const getBase64 = (file) =>
//   new Promise((resolve, reject) => {
//     const reader = new FileReader()
//     reader.readAsDataURL(file)
//     reader.onload = () => resolve(reader.result)
//     reader.onerror = (error) => reject(error)
//   })

export const isValidJson = (str) => {
  try {
    JSON.parse(str)
  } catch (e) {
    return false
  }
  return true
}

export function compare(prevProps, nextProps) {
  return stringify(prevProps) === stringify(nextProps)
}

export const compareWithJSON = (prevProps, nextProps) => {
  try {
    return JSON.stringify(prevProps) === JSON.stringify(nextProps)
  } catch (e) {
    console.error('error comparing with json', e)
    return true
  }
}

export function compareWithIsEqual(prevProps, nextProps) {
  return isEqual(prevProps, nextProps)
}

export function stringify(obj) {
  let cache = []
  const str = JSON.stringify(obj, (key, value) => {
    if (typeof value === 'object' && value !== null) {
      if (cache.indexOf(value) !== -1) {
        // Circular reference found, discard key
        return
      }
      // Store value in our collection
      cache.push(value)
    }
  })
  cache = null // reset the cache
  return str
}

export const stripTagsRDW = (blocks) => {
  const newBlocks = []

  if (isArray(blocks)) {
    blocks.forEach((block) => {
      newBlocks.push({
        ...block,
        // prevent link like below to be undefined and still link (can be click like link)
        // <a data-atyp="prec" jsaction="trigger.VM8bg" data-url="intent://www.google.com/maps/place//data=!4m2!3m1!1s0x2e69f700ca0f71ed:0xec2353a4f12e3357!11m1!4b1?entry=s&amp;sa=X&amp;ved=2ahUKEwjpmt3Vt6qCAxXCTGwGHSJOASkQ4kB6BAgBEAA#Intent;scheme=http;package=com.google.android.apps.maps;S.browser_fallback_url=https://www.google.com/maps/place//data%3D!4m2!3m1!1s0x2e69f700ca0f71ed:0xec2353a4f12e3357!11m1!4b1%3Fentry%3Ds&amp;sa%3DX&amp;ved%3D2ahUKEwjpmt3Vt6qCAxXCTGwGHSJOASkQ4kB6BAgBEAA;end" role="link" tabindex="0" data-ved="2ahUKEwjpmt3Vt6qCAxXCTGwGHSJOASkQ4kB6BAgWEAI" style="-webkit-tap-highlight-color: rgba(0, 0, 0, 0.1); background-color: rgb(255, 251, 255); max-height: 999999px; color: rgb(21, 88, 214); font-family: Roboto, &quot;Helvetica Neue&quot;, Arial, sans-serif; font-size: 14px; cursor: pointer;">
        entityRanges: [],
        text: block?.text?.replace(/(<([^>]+)>)/gi, '')?.replace('&nbsp;', ' ') ?? '',
      })
    })
  }

  return newBlocks
}

/**
 *
 * @param data {string[]}
 * @returns {string[]}
 */
export const sortDate = (data) => {
  return data.sort(
    (prevDate, nextDate) => new Date(prevDate).getTime() - new Date(nextDate).getTime(),
  )
}

// Reference https://css-tricks.com/converting-color-spaces-in-javascript/#aa-hex-to-rgb
/**
 *
 * @param h {string}
 * @returns {string}
 */
export const hexToRGB = (h) => {
  let r = 0
  let g = 0
  let b = 0

  // 3 digits
  if (h.length === 4) {
    r = `0x${h[1]}${h[1]}`
    g = `0x${h[2]}${h[2]}`
    b = `0x${h[3]}${h[3]}`

    // 6 digits
  } else if (h.length === 7) {
    r = `0x${h[1]}${h[2]}`
    g = `0x${h[3]}${h[4]}`
    b = `0x${h[5]}${h[6]}`
  }

  return `rgb(${+r},${+g},${+b})`
}

// Check contrast color for tag with background color
// Reference: https://stackoverflow.com/a/9733420

const RED = 0.2126
const GREEN = 0.7152
const BLUE = 0.0722

const GAMMA = 2.4

const luminance = (r, g, b) => {
  const a = [r, g, b].map((v) => {
    v /= 255
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** GAMMA
  })
  return a[0] * RED + a[1] * GREEN + a[2] * BLUE
}

/**
 *
 * @param stringRgb {string}
 * @returns {number[]}
 */
const extractRgb = (stringRgb) => {
  return stringRgb
    .split(/[()]/i)[1]
    .split(',')
    .map((num) => Number(num))
}

export const checkContrast = (rgb1, rgb2) => {
  const lum1 = luminance(...extractRgb(rgb1))
  const lum2 = luminance(...extractRgb(rgb2))
  const brightest = Math.max(lum1, lum2)
  const darkest = Math.min(lum1, lum2)
  return (brightest + 0.05) / (darkest + 0.05)
}

// Untuk mengganti bahasa dayjs dan calendar
export const changeLocale = (locale) => {
  const localeShort = locale === 'id-ID' ? 'id' : 'en'
  dayjs.locale(localeShort)
}

// Konversi hari termin ke due date
export const transformDueDate = (termId, transDate, terms) => {
  let dueDate = null
  if (!terms) {
    return dueDate
  }
  const cloneTransDate = transDate ? clone(dayjs(transDate.format('YYYY-MM-DD'))) : dayjs()
  const findTerm = terms.find((row) => row.id === termId)
  if (findTerm) {
    dueDate = cloneTransDate.add(findTerm.days, 'days')
  } else {
    dueDate = cloneTransDate.add(30, 'days')
  }

  return dueDate
}

export const decodeUriWithAtob = (search, isSearchParams = true) => {
  if (!isEmpty(search)) {
    let params = search
    if (isSearchParams) {
      const parseSearchParams = parse(search)
      params = parseSearchParams?.filters || ''
      if (typeof params === 'string') {
        params = params.replace(/ /g, '+')
      }
    }
    if (!isEmpty(params)) {
      try {
        const decodedString = atob(params || '')
        return JSON.parse(decodeURIComponent(decodedString))
      } catch {
        return {}
      }
    }
  }
  return {}
}

export const encodeUriWithBtoa = (params) => {
  if (!isEmpty(params)) {
    const queryString = JSON.stringify(params)
    const encodedString = encodeURIComponent(queryString)
    return btoa(encodedString)
  }
  return ''
}

export const getFileNameFromHeaders = (headers) => {
  const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
  const matches = filenameRegex.exec(headers['content-disposition'])
  if (matches?.[1]) {
    return matches[1].replace(/['"]/g, '')
  }

  return null
}

export const createDownloadLink = ({ data, headers }) => {
  const downloadLink = document.createElement('a')
  const blob = new Blob([data], { type: headers['content-type'] })
  downloadLink.href = URL.createObjectURL(blob)

  const fileName = getFileNameFromHeaders(headers)
  if (!isEmpty(fileName)) {
    downloadLink.download = fileName
  }
  downloadLink.click()
}

// Untuk mengambil qty dalam unit dasar
export const getBaseQtyUnit = ({ productUnits = [], unitId, qty }) => {
  let _qty = qty
  if (isEmpty(productUnits)) {
    return _qty
  }
  const productUnit = productUnits.find((row) => row.unit_id === unitId)
  if (productUnit?.unit_conv > 0 && !productUnit?.is_main) {
    let unitConv = productUnit.unit_conv
    if (typeof unitConv === 'string') {
      unitConv = parseFloat(unitConv)
    }
    _qty = qty * unitConv
  }
  return _qty
}

// Peta qty gabungan (base unit) per rule id, hanya untuk rule min_qty_mode = 1.
// Dipakai untuk mengevaluasi kelayakan promo yang minimal kuantitasnya dihitung
// dari total gabungan semua item dalam scope aturan (bukan per item).
export const getCombinedQtyByRuleId = (items = []) => {
  const map = {}
  items.forEach((item) => {
    if (!item || isEmpty(item.price_rules)) {
      return
    }
    const baseQty =
      Number(
        getBaseQtyUnit({
          productUnits: item.product_units,
          unitId: item.unit_id,
          qty: item.qty || 0,
        }),
      ) || 0
    item.price_rules.forEach((rule) => {
      if (rule.min_qty_mode === 1) {
        map[rule.id] = (map[rule.id] || 0) + baseQty
      }
    })
  })
  return map
}

export const convertToDecimalYears = (year, month) => {
  return year + month / 12
}

export const convertFromDecimalYears = (year) => {
  const newYear = parseInt(year, 10)
  const month = Math.round((year - newYear) * 12)

  return { year: newYear, month }
}

// Untuk penggunaan save params ke local storage dan mengecheck valid atau tidaknya date dengan mengexclude undefined
// agar jika tidak ada di local storage date tersebut tidak mereset defaultnya. Misalnya params date baru
export const nonEmptyValidDayjs = (date) => date && dayjs(date).isValid()

// Untuk mengecek apakah untuk perusahaan Batik dan shoppe
export const checkShoppeValidate = ({ websiteId, selectedTags }) => {
  if (websiteId === DUMMY_COMPANY_ID) {
    if (!isEmpty(selectedTags) && Array.isArray(selectedTags)) {
      const findShopee = find(selectedTags, (tag) => toLower(tag.name) === 'shopee')
      if (findShopee) {
        return true
      }
    }
  }
  return false
}

export const getRecurringTotal = ({ queryClient, queryKey }) => {
  const queries = queryClient.getQueriesData(queryKey)
  const queryRecurring =
    queries[queries.length - 1][1] ?? queries.filter((query) => !!query?.[1])?.[0]?.[1]
  return queryRecurring?.total || 0
}

export const validteName = ({
  value,
  validateCapital = false,
  capitalMessage,
  offensiveMessage,
}) => {
  if (!value) {
    return Promise.resolve()
  }

  // Validasi tidak boleh huruf besar semua
  if (validateCapital) {
    if (value === value.toUpperCase() && value.length > 0) {
      return Promise.reject(capitalMessage)
    }
  }

  // Validasi kata-kata kasar
  const offensiveWords = [
    'anjing',
    'fuck',
    'babi',
    'bangsat',
    'kontol',
    'memek',
    'jembut',
    'bajingan',
    'bangsat',
    'goblok',
    'bodoh',
    'tolol',
    'idiot',
    'dungu',
    'bego',
    'asu',
    'anjeng',
    'asoe',
    'an jing',
  ]
  const lowerValue = value.toLowerCase()

  const hasOffensiveWord = offensiveWords.some((word) => lowerValue.includes(word))
  if (hasOffensiveWord) {
    return Promise.reject(offensiveMessage)
  }

  return Promise.resolve()
}

export const parseFormatDropdownToArray = (formatDropdown, isRaw = false) => {
  if (!isEmpty(formatDropdown)) {
    // Mengubah string format [CODE]-[NAME]/[CATEGORY] menjadi array ['code', 'name', 'category']
    const formatString = formatDropdown
    const result = []

    // Mencari semua bagian yang diapit oleh []
    const matches = formatString.match(/\[([^\]]+)\]/g)

    if (matches) {
      matches.forEach((match) => {
        if (isRaw) {
          result.push(match)
        } else {
          // Menghapus tanda [] dan mengubah ke lowercase
          const field = match.replace(/[\[\]]/g, '').toLowerCase()
          result.push(field)
        }
      })
    }

    return result
  }
  return []
}

export const calculateWeightInKg = ({ productUnits = [], unitId, qty, weightInGram }) => {
  let _qty = qty
  if (!isEmpty(productUnits)) {
    const unit = productUnits.find((row) => row.unit_id === unitId)
    if (unit?.unit_conv > 1) {
      _qty *= parseFloat(unit.unit_conv)
    }
  }

  return (_qty * weightInGram) / 1000
}

export const calculateVolumeInMeter = ({ productUnits = [], unitId, qty, volume }) => {
  let _qty = qty
  if (!isEmpty(productUnits)) {
    const unit = productUnits.find((row) => row.unit_id === unitId)
    if (unit?.unit_conv > 1) {
      _qty *= parseFloat(unit.unit_conv)
    }
  }

  return (_qty * volume) / 1_000_000
}

export const formattedAttachments = (attachments) => {
  return attachments.map((url, index) => {
    let name = ''
    if (url) {
      const { pathname } = new URL(url)
      if (pathname) {
        const splitPath = pathname.split('/')
        name = splitPath ? decodeURI(last(splitPath)) : ''
      }
    }

    return {
      uid: `${index + 1}_id`,
      status: 'done',
      url,
      name,
    }
  })
}

export const hasDecimal = (value) => {
  return value.toString().includes('.') || value.toString().includes(',')
}

export const isProduction = () => {
  return import.meta.env.PROD
}

// Normalisasi nilai option dari BE yang tidak konsisten (string "1"/"0" atau number 1/0) menjadi boolean.
// Dipakai untuk pengecekan outletOptions / print_style_* yang tipenya bisa beda-beda.
export const isOptionEnabled = (value) => value?.toString() === '1'

export function isChunkLoadError(e) {
  const msg = String(e?.message || e?.reason || '')
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('ChunkLoadError') ||
    msg.includes('Loading chunk') ||
    msg.includes('Expected a JavaScript-or-Wasm module script')
  )
}

export const replaceWhatsappBody = (body) => {
  return body
    .replace(/(?:\r\n|\r|\n)/g, '%0a')
    .replace(/#/g, encodeURIComponent('#'))
    .replace(/&/g, encodeURIComponent('&'))
}

export const formatWhatsappPhone = (_phone) => {
  let phone = _phone
  if (phone.charAt(0) === '+') {
    phone = phone.substring(1)
  }
  if (phone.charAt(0) === '0') {
    phone = phone.substring(1)
    phone = `62${phone}`
  }
  return phone
}

export const removeBase64UrlPrefix = (url) => {
  if (!url) {
    return ''
  }

  return url.replace(/^data:[^;]+;base64,/, '')
}

export const getByPath = (obj, path) =>
  !path
    ? undefined
    : String(path)
        .split('.')
        .reduce((acc, p) => (acc == null ? acc : acc[p]), obj)

// generate card character by firstname and lastname
export const generateCardName = (name) => {
  const fullName = name.split(' ')
  if (fullName.length > 2) {
    return `${fullName[0].slice(0, 1)}${fullName[2].slice(0, 1)}`
  } else if (fullName.length > 1) {
    return `${fullName[0].slice(0, 1)}${fullName[1].slice(0, 1)}`
  } else {
    return `${fullName[0].slice(0, 1)}`
  }
}

// Konversi hex ke RGB
export const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null
}

// Membuat warna light untuk background (dengan opacity)
export const lightenColor = (hex, opacity = 0.2) => {
  const rgb = hexToRgb(hex)
  if (!rgb) {
    return hex
  }
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`
}

// Membuat warna dark untuk text (dengan opacity lebih tinggi)
export const darkenColor = (hex, opacity = 0.8) => {
  const rgb = hexToRgb(hex)
  if (!rgb) {
    return hex
  }
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`
}

// Generate warna konsisten berdasarkan nama produk
export const getColorForProduct = (name, colorPalette) => {
  if (!name || !colorPalette || colorPalette.length === 0) {
    const baseColor = colorPalette?.[0] || '#000000'
    return {
      bg: lightenColor(baseColor, 0.2),
      text: darkenColor(baseColor, 0.9),
    }
  }
  let hash = 0
  for (let i = 0; i < name.length; i += 1) {
    hash = name.charCodeAt(i) + (hash * 32 - hash)
  }
  const index = Math.abs(hash) % colorPalette.length
  const baseColor = colorPalette[index]
  return {
    bg: lightenColor(baseColor, 0.2),
    text: darkenColor(baseColor, 0.9),
  }
}

/**
 * Ambil query data secara general
 */
export const getQueryData = ({ queryClient, queryKey }) => {
  const queries = queryClient.getQueriesData(queryKey)
  return queries.at(-1) ?? queries.filter((query) => !!query?.[1])?.[0]
}

const BANK_LINK_ACCOUNT_CATEGORIES = [bankStaticConst.CASH_BANK]

const getAccountCategoryId = (account) => account?.credited_account?.account_category?.id

// Untuk format umur cache _cache_meta dari response laporan.
// Return { id, value } supaya consumer bisa pakai FormattedMessage tanpa intl dependency di helper.
export const getCacheAgeMessage = (seconds) => {
  const s = Number(seconds) || 0
  if (s < 60) {
    return { id: 'reports.cache.age_seconds', value: s }
  }
  if (s < 3600) {
    return { id: 'reports.cache.age_minutes', value: Math.floor(s / 60) }
  }
  if (s < 86400) {
    return { id: 'reports.cache.age_hours', value: Math.floor(s / 3600) }
  }
  return { id: 'reports.cache.age_days', value: Math.floor(s / 86400) }
}

export const compareBankRequestId = (data, isTransaction = false) => {
  const creditedAccountCategoryId = Number(getAccountCategoryId(data))
  const isCashBankAccount = BANK_LINK_ACCOUNT_CATEGORIES.includes(creditedAccountCategoryId)

  if (!isTransaction && !isCashBankAccount) {
    return ''
  }

  const bankAccountId =
    data.bank_account_id ||
    data.cash_bank_account_id ||
    data.origin_cash_bank_transaction?.bank_account_id ||
    data.cash_bank_transaction?.bank_account_id ||
    data.bank_transaction?.bank_account_id ||
    data.credited_account_id ||
    data.credited_account?.id ||
    data.account?.id

  if (!bankAccountId) {
    return ''
  }

  const transactionId = data.bank_tran_id

  if (!transactionId) {
    return { bankAccountId }
  }

  return { bankAccountId, transactionId }
}

// Menentukan apakah ref_number di form adalah nomor ketikan user (manual)
// atau sekadar echo dari preview nomor otomatis.
// .trim() hanya untuk perbandingan — nilai yang dikirim ke payload tidak di-trim.
export const isManualRefNumber = ({ refNumber, lastAutoRefNumber, disallowCustomNumber }) => {
  if (disallowCustomNumber && disallowCustomNumber.toString() === '1') {
    return false
  }

  return (refNumber || '').trim() !== (lastAutoRefNumber || '').trim()
}

export const safeParseJson = (jsonString, defaultValue) => {
  try {
    return JSON.parse(jsonString)
  } catch {
    return defaultValue
  }
}

/**
 * Outlet tempat pembayaran non tunai berlaku (`outlet_ids` atau `outlets[]` dari API).
 * Array kosong = berlaku di semua outlet.
 */
export const getCashlessOutletIds = (detail) => {
  let ids = []
  if (Array.isArray(detail?.outlet_ids)) {
    ids = detail.outlet_ids
  } else if (Array.isArray(detail?.outlets)) {
    ids = detail.outlets.map((row) => row?.id ?? row)
  }
  return uniq(ids.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0))
}

/**
 * Nama outlet tempat pembayaran non tunai berlaku: dari `outlets[]` API, fallback daftar outlet.
 */
export const getCashlessOutletNames = (detail, outlets = []) => {
  const apiOutlets = Array.isArray(detail?.outlets) ? detail.outlets : []
  return getCashlessOutletIds(detail).map((id) => {
    const found =
      apiOutlets.find((row) => Number(row?.id) === id && row?.name) ||
      outlets.find((row) => Number(row?.id) === id)
    return found?.name || `#${id}`
  })
}

/**
 * Nilai `outlet_ids` untuk payload simpan pembayaran non tunai.
 * Semua Outlet → `null` (tambah & ubah).
 */
export const getCashlessOutletPayload = (values) =>
  values.outlet_scope === 'specific' ? values.outlet_ids : null
